import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import type { ContactStatementKind } from '@accounting/shared';
import { useContactStatement } from '../../hooks/useContactStatement';
import { useActiveCompanyId } from '../../stores/useCompanyStore';
import { ContactPicker } from '../../components/contacts/ContactPicker';
import { formatAccountingDate, formatCurrency, todayIso } from '../../lib/formatters';

export const Route = createFileRoute('/_auth/statements/')({
  component: StatementsPage,
});

function StatementsPage() {
  const companyId = useActiveCompanyId();
  const [kind, setKind] = useState<ContactStatementKind>('customer');
  const [contactId, setContactId] = useState('');
  const [from, setFrom] = useState(`${todayIso().slice(0, 4)}-01-01`);
  const [to, setTo] = useState(todayIso());

  const { data: st, isLoading } = useContactStatement(companyId, kind, contactId, from, to);

  if (!companyId) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32 }}>
        <h1 style={{ marginTop: 0 }}>Statements</h1>
        <p className="muted">No active company.</p>
      </div>
    );
  }

  return (
    <div>
      <h1 style={{ fontSize: 24, margin: '0 0 4px' }}>Statements</h1>
      <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
        A customer or vendor account statement — charges, payments, and running balance for a period.
      </p>

      <div className="card no-print" style={{ padding: 12, marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={kind} onChange={(e) => { setKind(e.target.value as ContactStatementKind); setContactId(''); }}>
          <option value="customer">Customer</option>
          <option value="vendor">Vendor</option>
        </select>
        <div style={{ minWidth: 240 }}>
          <ContactPicker
            companyId={companyId}
            value={contactId}
            onChange={setContactId}
            contactType={kind}
            defaultNewType={kind}
            placeholder={`Select ${kind}…`}
          />
        </div>
        <label style={{ fontSize: 13 }}>From <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
        <label style={{ fontSize: 13 }}>To <input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></label>
        {st && <button className="btn-secondary" onClick={() => window.print()}>Print / Save PDF</button>}
      </div>

      {!contactId && <p className="muted">Pick a {kind} to view their statement.</p>}
      {contactId && isLoading && <p className="muted">Loading…</p>}

      {st && (
        <div className="card printable" style={{ padding: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
            <div>
              <h2 style={{ margin: 0, fontSize: 20 }}>{st.contactName}</h2>
              <div className="muted" style={{ fontSize: 13 }}>{kind === 'customer' ? 'Customer' : 'Vendor'} statement</div>
            </div>
            <div style={{ textAlign: 'right', fontSize: 13 }}>
              <div className="muted">Period</div>
              <div>{formatAccountingDate(st.from)} – {formatAccountingDate(st.to)}</div>
            </div>
          </div>

          <table className="table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Reference</th>
                <th style={{ textAlign: 'right' }}>Charge</th>
                <th style={{ textAlign: 'right' }}>Payment</th>
                <th style={{ textAlign: 'right' }}>Balance</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td colSpan={5} className="muted">Opening balance</td>
                <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatCurrency(st.openingBalance)}</td>
              </tr>
              {st.lines.map((l, i) => (
                <tr key={i}>
                  <td>{formatAccountingDate(l.date)}</td>
                  <td>{l.type}</td>
                  <td>{l.ref}</td>
                  <td style={{ textAlign: 'right' }}>{l.charge ? formatCurrency(l.charge) : ''}</td>
                  <td style={{ textAlign: 'right' }}>{l.credit ? formatCurrency(l.credit) : ''}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(l.balance)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
                <td colSpan={3}>Closing balance</td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(st.totalCharges)}</td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(st.totalCredits)}</td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(st.closingBalance)}</td>
              </tr>
            </tfoot>
          </table>

          <p className="muted" style={{ fontSize: 13, marginTop: 16 }}>
            {kind === 'customer' ? 'Amount due' : 'Amount payable'}: <strong>{formatCurrency(st.closingBalance)}</strong>
          </p>
        </div>
      )}
    </div>
  );
}
